/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Call, CallDirection, CallDisconnectStatus, CallStatus } from '../typings';
import { CallHeader } from './CallHeader';
import CallMeta from './CallMeta';
import { CallAction } from './CallActions';
import ReactDOM from 'react-dom';
import UserIdentityCard from '../../../shared/components/organisms/use-identity-card/UserIdentityCard';

const ExpandedView = ({
  call,
  isMuted,
  isSpeakerOff,
  toggleMute,
  toggleSpeaker,
  handleChatClick,
  onRequestAccept,
  onCloseChip,
  onMinimize,
  handleDisconnect,
}: {
  call: Call & { callStartTime?: number };
  isMuted: boolean;
  isSpeakerOff: boolean;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  handleChatClick: () => void;
  onRequestAccept: (call: Call, switchCall?: boolean) => void;
  onCloseChip: (call: Call) => void;
  onMinimize?: (call: Call) => void;
  handleDisconnect: (status: CallDisconnectStatus, reason?: string) => Promise<void>;
}) => {
  return ReactDOM.createPortal(
    <div
      role="dialog"
      css={css`
        position: fixed;
        right: 16px;
        top: 63px;
        width: 304px;
        background: #fff;
        border-radius: 16px;
        border: 1px solid var(--divider, rgba(30, 41, 59, 0.14));
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px 0 rgba(0, 0, 0, 0.05);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        z-index: 9999;
        ${call.status === CallStatus.INITIATED &&
        call.direction !== CallDirection.OUTGOING &&
        'animation: slideIn 0.3s ease-out;'}
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0%);
            opacity: 1;
          }
        }
      `}
      onClick={(e) => e.stopPropagation()}
    >
      <CallHeader
        state={call.status}
        direction={call.direction}
        wabaLabel={call.wabaName}
        wabaNumber={call.wabaNumber}
        onClose={() => onCloseChip(call)}
        onMinimize={() => onMinimize?.(call)}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px 16px 16px;
        `}
      >
        <UserIdentityCard
          dir={
            [CallStatus.IN_PROGRESS, CallStatus.Connecting, CallStatus.END, CallStatus.Ringing].includes(call.status)
              ? 'column'
              : 'row'
          }
          name={call.customerName || 'Unknown'}
          phoneNumber={call.customerNumber}
          size="large"
        />
      </div>
      {[CallStatus.IN_PROGRESS, CallStatus.END, CallStatus.Connecting, CallStatus.Ringing].includes(call.status) && (
        <CallMeta callStatus={call.status} startTime={call.callStartTime} />
      )}
      <CallAction
        callState={call.status}
        direction={call.direction}
        onAnswer={(switchCall) => onRequestAccept(call, switchCall)}
        onReject={() => handleDisconnect(CallStatus.END, 'Declined call')}
        onMuteToggle={toggleMute}
        onSpeakerToggle={toggleSpeaker}
        onChatClick={handleChatClick}
        onClose={() => onCloseChip(call)}
        isMuted={isMuted}
        isSpeakerOn={!isSpeakerOff}
        switchCall={call.switchCall}
      />
    </div>,
    document.body
  );
};

export default ExpandedView;
